import { CheckCircle2, Clock, FileText } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { formatDate } from '@/lib/utils';

import { useDocument, usePreview } from './api';
import HoursBadge from './hours-badge';
import { ConnectedDocument } from './types';

type EOPreviewProps = {
    document: ConnectedDocument;
};

const EOPreview = ({ document }: EOPreviewProps) => {
    const { data: url } = usePreview(document.id);
    const { data: eo } = useDocument(document.id);

    const manHours = {
        actual: eo?.meta?.raw?.actualManHours,
        estimated: eo?.meta?.raw?.estimatedManHours,
    };

    const isVerified = eo?.meta?.raw?.approvals?.some((a) => a.signed);

    return (
        <Card className="transition-all hover:shadow-md">
            <CardContent className="p-4 flex flex-col space-y-3">
                {/* Header */}
                <div className="flex flex-row items-center justify-between">
                    <div className="flex gap-2">
                        <Badge className="bg-zinc-100 text-zinc-700 font-medium">
                            {document.type}
                        </Badge>
                        <Badge className="bg-zinc-100 text-zinc-700 font-medium">
                            {document.source ? `Referenced by` : 'Refer to'}
                        </Badge>
                    </div>
                    {url && (
                        <a href={url} target="_blank" rel="noopener noreferrer">
                            <Button variant="default" size="sm">
                                Open
                            </Button>
                        </a>
                    )}
                </div>

                {/* Title and ID */}
                <div>
                    <h3 className="font-medium text-sm line-clamp-2 text-slate-800">
                        {eo?.meta?.raw?.title || 'Untitled Document'}
                    </h3>
                    <div className="text-xs text-slate-500">
                        {eo?.meta?.raw?.id}
                    </div>
                </div>

                {/* Aircraft Info */}
                <div className="flex items-center text-xs text-slate-500">
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    <span>
                        {eo?.meta?.raw?.aircraft?.registration ||
                            eo?.meta?.raw?.aircraft?.serialNumber ||
                            'No aircraft info'}
                    </span>
                </div>

                {/* Airline/MRO and Man Hours */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                        <div className="text-slate-500">Airline/MRO</div>
                        <div className="font-medium">
                            {eo?.meta?.raw?.issuingAuthority || 'N/A'}
                        </div>
                    </div>
                    <div>
                        <div className="text-slate-500">Man Hours</div>
                        <div className="font-medium">
                            {eo?.meta?.raw?.estimatedManHours
                                ? `${eo?.meta?.raw?.estimatedManHours} m/h`
                                : 'N/A'}
                        </div>
                    </div>
                </div>

                {/* Upload Date and Closing Date */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                        <div className="text-slate-500">Upload Date</div>
                        <div className="font-medium">
                            {eo?.createdAt ? formatDate(eo.createdAt) : 'N/A'}
                        </div>
                    </div>
                    <div>
                        <div className="text-slate-500">Closing Date</div>
                        <div className="font-medium">
                            {eo?.meta?.raw?.effectiveDate
                                ? formatDate(eo?.meta?.raw?.effectiveDate)
                                : 'N/A'}
                        </div>
                    </div>
                </div>

                {/* Priority and Compliance */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                        <div className="text-slate-500">Priority</div>
                        <div className="font-medium">
                            {eo?.meta?.raw?.priority || 'N/A'}
                        </div>
                    </div>
                    <div>
                        <div className="text-slate-500">Compliance</div>
                        <div className="font-medium">
                            {eo?.meta?.raw?.compliance?.category || 'N/A'}
                        </div>
                    </div>
                </div>

                {/* Linked Service Bulletins */}
                {eo?.meta?.raw?.references &&
                    eo?.meta?.raw?.references.length > 0 && (
                        <Collapsible>
                            <CollapsibleTrigger className="w-full">
                                <div className="flex items-center justify-between text-xs font-medium text-slate-700">
                                    <span>Linked Service Bulletins</span>
                                    <span className="text-slate-400">▼</span>
                                </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="mt-2">
                                <div className="flex flex-wrap gap-1">
                                    {eo?.meta?.raw?.references.map(
                                        (ref, index) => (
                                            <Badge
                                                key={index}
                                                variant="outline"
                                                className="text-xs"
                                            >
                                                {ref.referenceId ||
                                                    `Ref ${index + 1}`}
                                            </Badge>
                                        ),
                                    )}
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    )}

                {/* Tasks */}
                {eo?.meta?.raw?.tasks && eo?.meta?.raw?.tasks.length > 0 && (
                    <Collapsible>
                        <CollapsibleTrigger className="w-full">
                            <div className="flex items-center justify-between text-xs font-medium text-slate-700">
                                <span>
                                    Tasks ({eo?.meta?.raw?.tasks.length})
                                </span>
                                <span className="text-slate-400">▼</span>
                            </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2 space-y-1">
                            {eo?.meta?.raw?.tasks
                                .slice(0, 3)
                                .map((task, index) => (
                                    <div key={index} className="text-xs">
                                        <div className="font-medium">
                                            {task.taskId}
                                        </div>
                                        <div className="text-slate-500 line-clamp-1">
                                            {task.description}
                                        </div>
                                    </div>
                                ))}
                            {eo?.meta?.raw?.tasks.length > 3 && (
                                <div className="text-xs text-slate-400">
                                    +{eo?.meta?.raw?.tasks.length - 3} more
                                    tasks
                                </div>
                            )}
                        </CollapsibleContent>
                    </Collapsible>
                )}

                {/* Signatories */}
                {eo?.meta?.raw?.approvals &&
                    eo?.meta?.raw?.approvals.length > 0 && (
                        <div>
                            <div className="text-xs font-medium text-slate-700 mb-2">
                                Signatories
                            </div>
                            <div className="space-y-1">
                                {eo?.meta?.raw?.approvals.map(
                                    (approval, index) => (
                                        <div
                                            key={index}
                                            className="flex justify-between items-center text-xs"
                                        >
                                            <span className="font-medium">
                                                {approval.name || approval.role}
                                            </span>
                                            {approval.signed && (
                                                <Badge
                                                    variant="default"
                                                    className="bg-black text-white text-xs"
                                                >
                                                    signed
                                                </Badge>
                                            )}
                                        </div>
                                    ),
                                )}
                            </div>
                        </div>
                    )}

                {/* Footer */}
                <div className="flex flex-row justify-between items-center pt-2 border-t">
                    <HoursBadge
                        actualHours={manHours.actual ?? undefined}
                        estimatedHours={manHours.estimated ?? undefined}
                    />

                    {isVerified && (
                        <div className="flex items-center text-xs text-emerald-600">
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                            <span>Verified</span>
                        </div>
                    )}
                </div>

                {/* No data indicator */}
                {!eo?.meta?.raw && (
                    <div className="flex items-center text-xs text-slate-400">
                        <Clock className="h-3.5 w-3.5 mr-1" />
                        <span>No data</span>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default EOPreview;
